import { useState, useCallback, useEffect } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import {
  getVendorTaxonomyId,
  getVendorTaxonomyKeyFromId,
} from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import FinalFormDatePickerV2 from '@zola/zola-ui/src/components/Form/DatePickerInputV2/FinalFormDatePickerV2';
import { DropdownV2Adapter } from '@zola/zola-ui/src/components/Form/Dropdown';
import { FormInput } from '@zola/zola-ui/src/components/Form/InputFieldV2';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import cx from 'classnames';
import { Field, Form } from 'react-final-form';

import {
  BookVendorModalFormValues,
  createOrUpdateBookedVendorV2,
} from '~/actions/bookedVendorsActions';
import { getTaxonomy } from '~/actions/taxonomyActions';
import * as Validations from '~/components/common/form/commonValidations';
import { FormVendorAutosuggest } from '~/components/common/form/FinalForm/Inputs';
import useRegionCodes from '~/hooks/useRegionCodes';
import { getVendorTypeName } from '~/pages/couples/bookedVendors/components/BookedTile/helpers';
import {
  useBookedConfirmationModal,
  useBudgetResolutionModal,
} from '~/pages/couples/bookedVendors/utils/helper';
import { useAppDispatch, useAppSelector } from '~/reducers';
import type { RootState } from '~/reducers';
import { MappedVendorSearchResult } from '~/types/mappedResponseTypes';
import type { TaxonomyNodeView } from '~/types/responseTypes';
import { StateProvinceCodeType } from '~/types/types';
import { getMutator, setField, clearField } from '~/util/formMutators';
import { trackCtaClicked, TrackingProductLocation } from '~/util/trackingHelper';

import { NumberInputWithCommas } from '../zolaUI/NumberInputWithCommas/NumberInputWithCommas';

import styles from './bookVendorModal.module.less';

export interface BookVendorModalProps {
  hideModalFn: () => void;
  handleSuccess?: (arg0: BookVendorModalFormValues) => void;
  vendorTaxonomyKey?: VendorTaxonomyKey;
  // for tracking purposes
  section?: string;
  // for tracking purposes
  location?: TrackingProductLocation;
  linkToBookedVendorsPage?: boolean;
}

interface ReferenceVendorSearchSuggestion {
  suggestion: MappedVendorSearchResult;
}

const VendorNameFieldLabel = () => (
  <span className={styles.vendorNameFieldLabel}>
    Name<span className={styles.asterisk}>*</span>
  </span>
);

const getStateProvinceOptions = (usStates: Array<StateProvinceCodeType>) => {
  const stateProvinces = usStates;
  return stateProvinces.map(({ label, code: value }) => ({ value, label }));
};

const getSortedVendorTypeOptions = (taxonomyNode: TaxonomyNodeView) => {
  const { key } = taxonomyNode;
  return { value: key, label: getVendorTypeName(key) };
};

const BookVendorModal = ({
  hideModalFn,
  vendorTaxonomyKey,
  section,
  location,
  handleSuccess,
}: BookVendorModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { showBudgetResolutionModal } = useBudgetResolutionModal();
  const { showBookedConfirmationModal } = useBookedConfirmationModal();
  const { usStates = [] } = useRegionCodes();
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<number | undefined>();
  const [emailFromRefVendor, setEmailFromRefVendor] = useState(false);
  const [cityFromRefVendor, setCityFromRefVendor] = useState(false);
  const [stateFromRefVendor, setStateFromRefVendor] = useState(false);

  const ctaId =
    location === TrackingProductLocation.CATEGORY_LANDING_PAGE
      ? 'CLP_BOOKING_MODAL_SAVE_BUTTON'
      : 'LP_BOOKING_MODAL_SAVE_BUTTON';

  const cta = {
    ctaId,
    cta: '',
    location,
    ctaType: CtaType.BUTTON,
    position: 1,
    business_unit: BUSINESS_UNIT.MARKETPLACE,
    section,
  };

  const submit = (values: BookVendorModalFormValues) => {
    return dispatch(
      createOrUpdateBookedVendorV2(values, showBudgetResolutionModal, showBookedConfirmationModal)
    )
      .then(() => {
        if (handleSuccess) handleSuccess(values);
      })
      .finally(hideModalFn);
  };

  const vendorTypes: TaxonomyNodeView[] = useAppSelector(
    (state: RootState) => state.taxonomy.types
  );

  const getVendorTypeOptions = useCallback(() => {
    const sortedVendorTypes = vendorTypes.sort((a, b) => a.id - b.id);
    return sortedVendorTypes.map(getSortedVendorTypeOptions);
  }, [vendorTypes]);

  useEffectOnce(() => {
    dispatch(getTaxonomy()).catch(() => null);
  });

  useEffect(() => {
    if (vendorTaxonomyKey) {
      setSelectedTaxonomyId(getVendorTaxonomyId(vendorTaxonomyKey));
    }
  }, [vendorTaxonomyKey]);

  return (
    <div className={cx(styles.modal, styles.bookVendorModal)}>
      <Form<BookVendorModalFormValues>
        mutators={{
          setField: getMutator<BookVendorModalFormValues>(setField),
          clearField: getMutator<BookVendorModalFormValues>(clearField),
        }}
        onSubmit={(values) => {
          submit(values).finally(hideModalFn);
        }}
        initialValues={{ vendorType: vendorTaxonomyKey }}
        render={({ handleSubmit, form, values, submitting }) => {
          const { mutators } = form;
          return (
            <form className={styles.modalContent} onSubmit={handleSubmit as () => void}>
              <div className={styles.modalBody}>
                <h3>
                  Look at you go!{' '}
                  <span role="img" aria-label="party popper emoji">
                    🎉
                  </span>
                </h3>
                <p>Share their info below&mdash;we&apos;ll help you keep track of your team.</p>
                <hr />
                <fieldset className={styles.fieldset}>
                  <FinalFormDatePickerV2
                    name="eventDate"
                    id="eventDate"
                    label="Event date"
                    className={cx(styles.oneColumn, styles.datePicker)}
                  />
                  <Field
                    className={cx(styles.twoColumns, styles.dropdownV3)}
                    label="Who are they?"
                    name="vendorType"
                    id="vendorType"
                    options={getVendorTypeOptions()}
                    component={DropdownV2Adapter}
                    required
                    validate={Validations.REQUIRED}
                    placeholder="They are a..."
                    showToggleAnimation
                    onSelect={(value: string) => {
                      setSelectedTaxonomyId(getVendorTaxonomyId(value));
                    }}
                    disabled={Boolean(
                      (values.referenceVendorId && values.vendorType) || vendorTaxonomyKey
                    )}
                    // 👇hack for not allowing a user to clear typed input
                    onChange={() => null}
                  />
                  <span className={cx(styles.threeColumns, styles.vendorAutosuggest)}>
                    <FormVendorAutosuggest
                      name="vendorName"
                      includeActionLink={false}
                      limitToCategory={Boolean(selectedTaxonomyId)}
                      categoryId={selectedTaxonomyId}
                      useLabel
                      inputV2
                      labelText={<VendorNameFieldLabel />}
                      useVendorObject
                      placeholder="Their business is called..."
                      validate={Validations.REQUIRED}
                      onSuggestionSelected={(
                        event: React.SyntheticEvent,
                        { suggestion }: ReferenceVendorSearchSuggestion
                      ) => {
                        const {
                          id: referenceVendorId,
                          name,
                          email,
                          address,
                          taxonomyNodeId,
                        } = suggestion;
                        mutators.setField('referenceVendorId', referenceVendorId);
                        mutators.setField('vendorName', name);
                        if (email) {
                          mutators.setField('email', email);
                          setEmailFromRefVendor(true);
                        }
                        if (address.city) {
                          mutators.setField('city', address.city);
                          setCityFromRefVendor(true);
                        }
                        if (address.stateProvince) {
                          mutators.setField('state', address.stateProvince);
                          setStateFromRefVendor(true);
                        }
                        if (taxonomyNodeId) {
                          setSelectedTaxonomyId(taxonomyNodeId);
                          mutators.setField(
                            'vendorType',
                            getVendorTaxonomyKeyFromId(taxonomyNodeId)
                          );
                        }
                      }}
                      onInputChange={(value: string) => {
                        mutators.setField('vendorName', value);
                        if (value !== values.vendorName && values.referenceVendorId) {
                          [
                            setEmailFromRefVendor,
                            setCityFromRefVendor,
                            setStateFromRefVendor,
                          ].forEach((setter) => setter(false));
                          ['referenceVendorId', 'city', 'state', 'email'].forEach((field) =>
                            mutators.clearField(field)
                          );
                          if (!vendorTaxonomyKey) {
                            mutators.clearField('vendorType');
                            setSelectedTaxonomyId(undefined);
                          }
                        }
                      }}
                    />
                  </span>
                  <FormInput
                    id="city"
                    name="city"
                    label="City"
                    isRequired
                    allowChangeToEmpty
                    placeholder="They're in..."
                    disabled={values.referenceVendorId && cityFromRefVendor}
                    validate={Validations.REQUIRED}
                    className={styles.twoColumns}
                  />
                  <Field
                    className={cx(styles.oneColumn, styles.dropdownV3)}
                    label="State"
                    name="state"
                    id="state"
                    options={getStateProvinceOptions(usStates)}
                    component={DropdownV2Adapter}
                    required
                    validate={Validations.REQUIRED}
                    placeholder="Choose"
                    showToggleAnimation
                    disabled={Boolean(values.referenceVendorId && stateFromRefVendor)}
                    // 👇hack for not allowing a user to clear typed input
                    onChange={() => null}
                  />
                  <FormInput
                    id="email"
                    name="email"
                    label="Email"
                    disabled={values.referenceVendorId && emailFromRefVendor}
                    allowChangeToEmpty
                    validate={Validations.EMAIL}
                    placeholder="They're reachable at..."
                    className={styles.twoColumns}
                  />
                  <NumberInputWithCommas
                    name="price"
                    className={styles.oneColumn}
                    label="Total spend"
                    id="price"
                    displayedField="priceAsCurrency"
                    type="currency"
                  />
                </fieldset>
              </div>
              <div className={styles.modalControls}>
                <ButtonV2 className="mr-tertiary" variant="secondary" onClick={hideModalFn}>
                  Cancel
                </ButtonV2>
                <ButtonV2
                  onClick={() => trackCtaClicked(cta)}
                  role="button"
                  type="submit"
                  disabled={submitting}
                >
                  Save
                </ButtonV2>
              </div>
            </form>
          );
        }}
      />
    </div>
  );
};

export default BookVendorModal;
